import { Share2 } from 'lucide-react-native';
import { StyleSheet, View } from 'react-native';

import { DealAvailabilityBar } from '@/components/AvailabilityBar';
import { BadgePill } from '@/components/BadgePill';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { HeroWatermark } from '@/components/HeroWatermark';
import { Overline } from '@/components/Overline';
import { Text } from '@/components/Text';
import { colors, fonts, radii, spacing } from '@/constants/theme';
import { formatPromoEndDate } from '@/lib/promoDate';
import type { Deal } from '@/types/deal';

interface HeroEarningsCardProps {
  earnedCents: number;
  offerName?: string;
  perReferralDollars?: number;
  caption?: string;
  actionLabel?: string;
  onShare?: () => void;
  /**
   * The hero deal. When it carries a monthly cap, the availability bar
   * renders below the caption; when it's `full`, the Share CTA is disabled
   * (the server blocks the mint anyway — this keeps the UI honest).
   */
  deal?: Deal;
}

/**
 * Refer tab hero (v3, 2026-05-11): "YOU'VE EARNED $620" with a
 * Share my link CTA. Replaces the prior per-deal "EARN $20" framing —
 * v3 emphasizes accumulated earnings over per-referral payout to push
 * the leaderboard psychology.
 */
export function HeroEarningsCard({
  earnedCents,
  offerName,
  perReferralDollars,
  caption,
  actionLabel = 'Share my link',
  onShare,
  deal,
}: HeroEarningsCardProps) {
  const dollars = (earnedCents / 100).toFixed(0);
  const capped = deal?.monthly_referral_cap != null;
  const full = deal?.full ?? false;
  // Live referral payout boost (server fields only — the client never does
  // rate math). `referrer_cut_cents` already carries the BOOSTED amount;
  // `base_referrer_cut_cents` is the unboosted one, so boosted means the
  // effective cut actually beats base. Optional because older servers /
  // cached payloads don't send the base fields — then this is simply false.
  const baseCutCents = deal?.base_referrer_cut_cents;
  // Floor to match the caller's perReferralDollars math (referrals.tsx) —
  // mixed rounding rules could strike through a number identical to the
  // boosted one (e.g. $39.90 rounding up to the $40 it sits next to). And
  // if the two DISPLAYED integers still collide on a sub-dollar boost,
  // showing the strikethrough over an unchanged number reads as a glitch —
  // suppress it.
  const rawBoosted =
    deal != null && baseCutCents != null && deal.referrer_cut_cents > baseCutCents;
  const flooredBase = rawBoosted ? Math.floor(baseCutCents / 100) : null;
  const boosted = rawBoosted && flooredBase !== perReferralDollars;
  const baseDollars = boosted ? String(flooredBase) : null;
  const bonusEndsAt = boosted ? (deal.referrer_bonus_ends_at ?? null) : null;
  // "No cap." is only true for an uncapped deal — drop it once a cap exists,
  // since the availability bar now states the monthly limit.
  const bodyTail = `every time someone signs up to ${offerName} through your link.${capped ? '' : ' No cap.'}`;
  const body = caption ?? `$${perReferralDollars} ${bodyTail}`;
  return (
    <Card variant="dark" style={styles.card}>
      <HeroWatermark />
      <Overline tone="onDark">You&apos;ve earned</Overline>
      <Text style={styles.amount}>${dollars}</Text>
      {boosted && caption == null ? (
        // Boosted rate: base struck through before the boosted amount, both
        // straight off the serializer. Nested Texts inherit the caption style.
        <Text
          variant="bodyMedium"
          color="textOnDarkMuted"
          style={[styles.caption, styles.captionTight]}
          // VoiceOver reads nested text as one run — "$20 $40 every time…" —
          // with no way to hear that the first number is struck through.
          accessibilityLabel={`Now $${perReferralDollars}, up from $${baseDollars}, ${bodyTail}`}
        >
          <Text variant="bodyMedium" color="textOnDarkMuted" style={styles.strike}>
            {`$${baseDollars}`}
          </Text>
          {' '}
          <Text variant="bodyMedium" color="frost" style={styles.boostedRate}>
            {`$${perReferralDollars}`}
          </Text>
          {` ${bodyTail}`}
        </Text>
      ) : (
        <Text variant="bodyMedium" color="textOnDarkMuted" style={styles.caption}>
          {body}
        </Text>
      )}
      {boosted ? (
        <View style={styles.bonusRow}>
          <BadgePill label={deal.referrer_bonus_badge} testID="hero-bonus-badge" />
          {bonusEndsAt ? (
            <Text variant="caption" color="frost">
              {`Double payouts end ${formatPromoEndDate(bonusEndsAt)}`}
            </Text>
          ) : null}
        </View>
      ) : null}
      {deal ? <DealAvailabilityBar deal={deal} tone="dark" style={styles.bar} /> : null}
      {onShare ? (
        <Button
          variant="primary"
          size="base"
          fullWidth
          label={full ? 'Full this month' : actionLabel}
          onPress={onShare}
          disabled={full}
          leadingIcon={full ? undefined : Share2}
        />
      ) : null}
    </Card>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: spacing.lg,
    borderRadius: radii['2xl'],
    overflow: 'hidden',
  },
  amount: {
    fontFamily: fonts.numeric,
    fontSize: 56,
    lineHeight: 60,
    color: colors.electricGreen,
    marginTop: spacing.xs,
  },
  caption: {
    marginTop: spacing.xs,
    marginBottom: spacing.base,
  },
  // Old price: the strike must be unmissable (Jason's launch-eve note) — a
  // muted line on muted text disappears on the deep-green card, so the line
  // itself renders in frost while the digits stay muted. New price: frost +
  // bold beside it, so "was/now" reads at a glance.
  strike: {
    textDecorationLine: 'line-through',
    textDecorationColor: colors.frost,
  },
  boostedRate: {
    fontFamily: fonts.bodyBold,
  },
  // The bonus row directly below carries the block's bottom margin, so the
  // caption gives its own up — stacked margins would double the gap.
  captionTight: {
    marginBottom: spacing.xs,
  },
  bonusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.base,
  },
  bar: {
    marginBottom: spacing.base,
  },
});
